import "@cyntler/react-doc-viewer/dist/index.css";
import { faFileDownload, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, ReactNode, useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertProps,
  Button,
  Col,
  Container,
  Row,
} from "react-bootstrap";
import useDownloadCaseAttachment from "../../../../modules/filestore/hooks/useDownloadCaseAttachment";
import { EUFinanceCaseAttachment } from "../../../../modules/filestore/models/sobjects/EUFinanceCaseAttachment";
import { openFileInNewTab } from "../../../../modules/filestore/utils/FileUtil";
import FileViewer from "./components/FileViewer";

const CLASSNAME = "eu-finance-case-attachment-viewer__case-attachment-viewer";

export type DownloadCaseAttachmentProps = {
  caseAttachment: EUFinanceCaseAttachment;
};

const CaseAttachmentViewer: FC<DownloadCaseAttachmentProps> = ({
  caseAttachment,
}) => {
  const [alertVariant, setAlertVariant] =
    useState<AlertProps["variant"]>("info");
  const [alertBody, setAlertBody] = useState<ReactNode>();

  const onError = useCallback((message: ReactNode) => {
    setAlertVariant("danger");
    setAlertBody(message);
  }, []);

  const onDownloadStart = useCallback((message: ReactNode) => {
    setAlertVariant("info");
    setAlertBody(message);
  }, []);

  const onDownloadComplete = useCallback((message: ReactNode) => {
    setAlertVariant("success");
    setAlertBody(message);
  }, []);

  const { isLoading, downloadFile, blob, fileExtension, fileName, mimeType } =
    useDownloadCaseAttachment({
      onError,
      onDownloadStart,
      onDownloadComplete,
    });

  useEffect(() => {
    if (caseAttachment.id && downloadFile) {
      downloadFile(caseAttachment);
    }
  }, [caseAttachment, downloadFile]);

  return (
    <div className={CLASSNAME}>
      <Container fluid>
        <Row>
          <Col className="mt-3">
            <Alert variant={alertVariant}>
              {alertBody ?? (
                <>
                  Downloading <b>{caseAttachment?.fileName}</b>...
                </>
              )}
            </Alert>
          </Col>
        </Row>
        <Row>
          <Col>
            <Button
              variant="success"
              className="text-white me-2"
              onClick={() =>
                blob &&
                openFileInNewTab(blob, fileExtension, fileName, mimeType)
              }
            >
              <FontAwesomeIcon icon={faFileDownload} className="me-2" />
              Download
            </Button>
          </Col>
        </Row>
        <Row>
          <Col className="mt-3">
            {isLoading ? (
              <div
                className={`${CLASSNAME}__loading d-flex justify-content-center align-items-center`}
              >
                <FontAwesomeIcon icon={faSpinner} spin size="10x" />
              </div>
            ) : (
              <FileViewer
                blob={blob}
                fileExtension={fileExtension}
                fileName={fileName}
              />
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CaseAttachmentViewer;
